import { creatureIcon, creatureSprite } from "../art/creatures.ts";
import { enterAge, settleT } from "../art/craftFeel.ts";
import { TYPE_COL, UI, withA } from "../art/pal.ts";
import { SPECIES } from "../data/species.ts";
import { fitText, measureText, wrapPx } from "../engine/font.ts";
import { SCREEN_H, type Gfx } from "../engine/gfx.ts";
import type { Ctx } from "../game/ctx.ts";
import type { Mon } from "../game/mon.ts";
import type { Scene } from "../game/scenes.ts";
import type { TradeView } from "../net/protocol.ts";
import { backdrop, coin, focus, glass, hint, titleBar, typeBadge } from "../ui/kit.ts";

const ROWS = 6;
const ACTIONS = ["MONEY", "LOCK", "CONFIRM", "CANCEL"] as const;

/**
 * Live trade window. The server owns the offer: every toggle is sent and the view comes back, so
 * both screens always show the same thing. Nothing moves until both sides lock and confirm.
 */
export function tradeScene(ctx: Ctx): Scene {
  let cursor = 0;
  let scroll = 0;
  let age = 0;
  let result: { title: string; lines: string[] } | null = null;
  let view: TradeView | null = null;
  let unsub: () => void = () => {};

  const mine = (): Mon[] => [...ctx.state.party, ...ctx.state.pc];

  function offer(uids: string[], money: number): void {
    if (!view) return;
    ctx.net.send({ t: "tradeOffer", id: view.id, uids, money });
  }

  return {
    id: "trade",
    native: true,
    enter() {
      age = enterAge(ctx.shot);
      view = ctx.net.trade;
      result = null;
      cursor = scroll = 0;
      unsub = ctx.net.on((m) => {
        if (m.t === "trade") view = m.view;
        if (m.t === "tradeDone") {
          ctx.goal("trade");
          ctx.audio.catchOk();
          ctx.audio.jingle("victory", null);
          const got = m.got.map((x) => `${x.nick} Lv${x.level}`);
          result = {
            title: "TRADE COMPLETE",
            lines: [
              got.length ? `You got ${got.join(", ")}.` : "You got no creatures.",
              m.money > 0 ? `+${m.money} coins` : m.money < 0 ? `${m.money} coins` : "",
              "Saved to your crew.",
            ].filter(Boolean),
          };
        }
        if (m.t === "tradeClosed") {
          ctx.audio.back();
          result = { title: "TRADE CLOSED", lines: [m.reason] };
        }
      });
      if (!view) result = { title: "TRADE CLOSED", lines: ["The trade is no longer open."] };
    },
    exit() {
      unsub();
      if (view && ctx.net.trade?.id === view.id && !result) ctx.net.send({ t: "tradeCancel", id: view.id });
      ctx.net.trade = null;
    },
    update() {
      age += ctx.dt;
      if (result) {
        if (ctx.input.consumeAny("Enter", "Escape")) {
          ctx.audio.confirm();
          ctx.pop();
        }
        return;
      }
      if (!ctx.net.live) {
        result = { title: "TRADE CLOSED", lines: ["Connection lost. Nothing changed."] };
        return;
      }
      if (!view) return;
      const list = mine();
      const n = list.length + ACTIONS.length;
      const d = ctx.input.menuDir(ctx.now);
      const onMoney = cursor === list.length;
      if (d?.y) {
        cursor = (cursor + d.y + n) % n;
        ctx.audio.cursor();
      } else if (d?.x && onMoney && !view.locked.mine) {
        const stepAmt = ctx.input.down.has("Shift") ? 100 : 10;
        const money = Math.max(0, Math.min(ctx.state.money, view.mine.money + d.x * stepAmt));
        if (money !== view.mine.money) {
          ctx.audio.cursor();
          offer(view.mine.mons.map((m) => m.uid), money);
        }
      }
      if (cursor < list.length) {
        if (cursor < scroll) scroll = cursor;
        if (cursor >= scroll + ROWS) scroll = cursor - ROWS + 1;
      }
      if (ctx.input.consume("Escape")) {
        ctx.audio.back();
        ctx.net.send({ t: "tradeCancel", id: view.id });
        return;
      }
      if (!ctx.input.consume("Enter")) return;
      if (cursor < list.length) {
        if (view.locked.mine) {
          ctx.audio.fail();
          return;
        }
        const uid = list[cursor]!.uid;
        const offered = view.mine.mons.map((m) => m.uid);
        const next = offered.includes(uid) ? offered.filter((u) => u !== uid) : [...offered, uid].slice(0, 6);
        ctx.audio.confirm();
        offer(next, view.mine.money);
        return;
      }
      const action = ACTIONS[cursor - list.length]!;
      if (action === "LOCK") {
        ctx.audio.confirm();
        ctx.net.send({ t: "tradeLock", id: view.id, locked: !view.locked.mine });
      } else if (action === "CONFIRM") {
        if (!view.locked.mine || !view.locked.theirs) {
          ctx.audio.fail();
          return;
        }
        ctx.audio.confirm();
        ctx.net.send({ t: "tradeConfirm", id: view.id });
      } else if (action === "CANCEL") {
        ctx.audio.back();
        ctx.net.send({ t: "tradeCancel", id: view.id });
      }
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      const t = settleT(age, reduced, 120);
      backdrop(g, ctx.now, reduced, [24, 50, 64, 255], [10, 12, 24, 255]);
      const v = view;
      titleBar(g, `TRADE  ${v?.peer.name ?? ""}`, v ? (v.confirmed.mine && !v.confirmed.theirs ? "waiting" : v.locked.mine && v.locked.theirs ? "both locked" : "") : "", UI.teal);
      if (!v) {
        if (result) drawResult(g, result, ctx.now, reduced);
        return;
      }
      const list = mine();
      const offered = new Set(v.mine.mons.map((m) => m.uid));
      const lx = 8 - Math.round((1 - t) * 30);

      // Left: your creatures.
      glass(g, lx, 28, 184, 150, { r: 8 });
      g.textShadow(lx + 10, 34, "YOUR CREATURES", UI.textDim, UI.ink);
      g.textR(lx + 174, 34, `${offered.size}/6 offered`, offered.size ? UI.goldHi : UI.textMute);
      for (let r = 0; r < ROWS; r++) {
        const i = scroll + r;
        const m = list[i];
        if (!m) break;
        const y = 46 + r * 21;
        const on = cursor === i;
        const picked = offered.has(m.uid);
        g.rrect(lx + 6, y, 172, 19, 5, picked ? withA(UI.gold, 60) : on ? withA(UI.bg3, 220) : withA(UI.bg1, 160));
        g.blit(creatureIcon(m.speciesId, !!m.shiny), lx + 9, y + 1);
        g.text(lx + 30, y + 6, fitText(`${on ? ">" : ""}${m.nick}`, 84), on ? UI.goldHi : UI.text);
        g.textR(lx + 146, y + 6, `Lv${m.level}`, UI.gold);
        g.rrect(lx + 152, y + 4, 11, 11, 3, picked ? UI.gold : UI.bg0);
        if (picked) g.text(lx + 155, y + 6, "x", UI.paperInk);
        if (i >= ctx.state.party.length) g.text(lx + 166, y + 6, "B", UI.textMute);
        if (on) focus(g, lx + 5, y - 1, 174, 21, ctx.now, reduced, 6);
      }
      if (scroll > 0) g.text(lx + 88, 41, "▲", UI.textDim);
      if (scroll + ROWS < list.length) g.text(lx + 88, 172, "▼", UI.textDim);

      // Right: both offers.
      const rx = 198 + Math.round((1 - t) * 30);
      offerCard(g, rx, 28, "YOUR OFFER", v.mine.mons, v.mine.money, v.locked.mine, v.confirmed.mine);
      offerCard(g, rx, 104, `${v.peer.name.toUpperCase()} OFFERS`, v.theirs.mons, v.theirs.money, v.locked.theirs, v.confirmed.theirs);

      // Bottom actions.
      const ay = 184;
      const labels = [`COINS  ${v.mine.money}`, v.locked.mine ? "UNLOCK" : "LOCK", "CONFIRM", "CANCEL"];
      let ax = 8;
      labels.forEach((label, k) => {
        const idx = list.length + k;
        const on = cursor === idx;
        const disabled = (k === 2 && !(v.locked.mine && v.locked.theirs)) || (k === 0 && v.locked.mine);
        const w = measureText(label) + (k === 0 ? 32 : 18);
        const fill = on ? (k === 3 ? UI.red : UI.gold) : UI.bg2;
        g.rrect(ax, ay, w, 20, 6, UI.ink);
        g.rrect(ax, ay, w, 19, 6, fill);
        if (k === 0) coin(g, ax + 6, ay + 5, 0);
        g.text(ax + (k === 0 ? 20 : 9), ay + 6, label, on ? UI.paperInk : disabled ? UI.textMute : UI.text);
        if (on) focus(g, ax - 1, ay - 1, w + 2, 21, ctx.now, reduced, 7);
        ax += w + 6;
      });
      const onMoney = cursor === list.length;
      hint(g, 272, SCREEN_H - 30, "A", onMoney ? "◀▶ amount" : cursor < list.length ? "offer" : "select", "a");
      hint(g, 272, SCREEN_H - 16, "B", "cancel trade", "b");
      if (result) drawResult(g, result, ctx.now, reduced);
    },
  };
}

function offerCard(g: Gfx, x: number, y: number, title: string, mons: Mon[], money: number, locked: boolean, confirmed: boolean): void {
  glass(g, x, y, 178, 70, { r: 8, border: confirmed ? UI.green : locked ? UI.gold : UI.line });
  g.textShadow(x + 10, y + 6, fitText(title, 110), UI.textDim, UI.ink);
  const tag = confirmed ? "CONFIRMED" : locked ? "LOCKED" : "EDITING";
  const tw = measureText(tag) + 8;
  g.rrect(x + 170 - tw, y + 4, tw, 11, 4, confirmed ? UI.green : locked ? UI.gold : UI.bg3);
  g.text(x + 174 - tw, y + 6, tag, confirmed || locked ? UI.paperInk : UI.textDim);
  if (!mons.length) g.text(x + 10, y + 24, "No creatures.", UI.textMute);
  if (mons.length === 1) {
    // One creature: a proper card with the front sprite, level and both types.
    const m = mons[0]!;
    const sp = SPECIES[m.speciesId];
    g.disc(x + 26, y + 36, 15, withA(TYPE_COL[sp.types[0]!]!.fill, 70));
    g.blit(creatureSprite(m.speciesId, "front", 1, !!m.shiny), x + 10, y + 19);
    g.textShadow(x + 48, y + 22, fitText(m.nick, 80), UI.text, UI.ink);
    g.text(x + 132, y + 22, `Lv${m.level}`, UI.gold);
    let bx = x + 48;
    for (const type of sp.types) bx += typeBadge(g, bx, y + 34, type, true) + 2;
  } else {
    mons.slice(0, 6).forEach((m, i) => {
      const cx = x + 8 + (i % 3) * 56, cy = y + 20 + Math.floor(i / 3) * 18;
      const sp = SPECIES[m.speciesId];
      g.disc(cx + 9, cy + 8, 8, withA(TYPE_COL[sp.types[0]!]!.fill, 70));
      g.blit(creatureIcon(m.speciesId, !!m.shiny), cx + 1, cy);
      g.text(cx + 20, cy + 1, fitText(m.nick, 34), UI.text);
      g.text(cx + 20, cy + 9, `Lv${m.level}`, UI.gold);
    });
  }
  coin(g, x + 10, y + 57, 0);
  g.text(x + 23, y + 58, `${money} coins`, money ? UI.goldHi : UI.textMute);
}

function drawResult(g: Gfx, r: { title: string; lines: string[] }, now: number, reduced: boolean): void {
  g.fill(0, 0, 384, 216, [6, 6, 16, 150]);
  glass(g, 72, 60, 240, 96, { r: 10, border: r.title === "TRADE COMPLETE" ? UI.green : UI.line });
  g.textShadow(86, 70, r.title, r.title === "TRADE COMPLETE" ? UI.green : UI.text, UI.ink);
  let y = 88;
  for (const line of r.lines) {
    for (const row of wrapPx(line, 212).slice(0, 3)) {
      g.text(86, y, row, UI.text);
      y += 11;
    }
    y += 3;
  }
  hint(g, 86, 138, "A", "back to the street", "a");
  void now;
  void reduced;
}
